import type { DownloadItem } from '@atlas-control/shared';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Download, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { api, buildUrl } from '@/lib/api';

export const Route = createFileRoute('/_app/runs/$runId/downloads')({
  component: DownloadsTab,
});

function DownloadsTab() {
  const { runId } = Route.useParams();
  const q = useQuery({
    queryKey: ['runs', 'downloads', runId],
    queryFn: () => api<{ items: DownloadItem[] }>(`/runs/${runId}/downloads`).then((r) => r.items),
  });
  if (q.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const items = q.data ?? [];
  const docs = items.filter((i) => i.kind === 'document');
  const stages = items.filter((i) => i.kind === 'stage');

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Documents</CardTitle>
        </CardHeader>
        <CardContent>
          <Rows runId={runId} items={docs} />
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Stage JSON</CardTitle>
        </CardHeader>
        <CardContent>
          {stages.length > 0 && !stages.some((s) => s.allowed) && (
            <p className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
              <Lock className="size-3.5" /> Stage downloads are for developers. You can see which
              stages were reached.
            </p>
          )}
          <Rows runId={runId} items={stages} />
        </CardContent>
      </Card>
    </div>
  );
}

function Rows({ runId, items }: { runId: string; items: DownloadItem[] }) {
  return (
    <ul className="divide-y">
      {items.map((i) => {
        const href = buildUrl(`/runs/${runId}/downloads/${i.key}`, {
          pillar: i.pillarId ?? undefined,
        });
        return (
          <li
            key={`${i.key}:${i.pillarId ?? ''}`}
            className="flex items-center justify-between gap-3 py-2 text-sm"
          >
            <span className="min-w-0">
              <span className="block truncate">{i.label}</span>
              {i.reason && <span className="text-xs text-muted-foreground">{i.reason}</span>}
            </span>
            {i.allowed && (
              <Button asChild={i.available} variant="outline" size="sm" disabled={!i.available}>
                {i.available ? (
                  <a href={href} download>
                    <Download /> Download
                  </a>
                ) : (
                  <span>
                    <Download /> Download
                  </span>
                )}
              </Button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
